import {
  Button,
  DialogContent,
  DialogTitle,
  Modal,
  ModalClose,
  ModalDialog,
  DialogActions,
  Stack,
} from "@mui/joy";
import { useTranslation } from "../../../i18n";
import {
  useSkuListQuery,
  useSkuUpsertMutation,
} from "../../../__generated__/types-and-hooks";
import { useCallback, useEffect, useState } from "react";
import { useTeam } from "../TeamLayout";
import { useNotify } from "../../../hooks/useNotify";
import { Sku$ } from "./Sku$";
import { XTextInput } from "../../../x-components/XTextInput";

export interface SkuAddModalProps {
  open: boolean;
  onClose: () => void;
}

export default function SkuAddModal({ open, onClose }: SkuAddModalProps) {
  const teamCtx = useTeam();
  const [sku$, setSku$] = useState<Sku$>(() => new Sku$());
  useEffect(() => {
    setSku$(new Sku$());
  }, [open]);

  const t = useTranslation();
  const [skuUpsertMutation, { loading }] = useSkuUpsertMutation();
  const skuListQuery = useSkuListQuery({
    variables: { teamId: teamCtx.team.id },
  });

  const [saving, setSaving] = useState(false);
  const notify = useNotify();

  const handleSave = useCallback(async () => {
    setSaving(true);
    sku$.json$
      .subscribe({
        next: async (data) => {
          setSaving(true);

          try {
            const res = await skuUpsertMutation({
              variables: {
                updates: {
                  teamId: teamCtx.team.id,
                  sku: data.sku,
                  name: data.name,
                  description: data.description,
                  category: data.category,
                  material: data.material,
                  asin: data.asin,
                  hs: data.hs,
                  hts: data.hts,
                  upc: data.upc,
                },
              },
              onCompleted() {
                skuListQuery.refetch();
              },
            });

            setSaving(false);
            onClose();
            if (res.errors) {
              notify({ type: "danger", msg: "An error has been occurred." });
            } else {
              notify({
                type: "success",
                msg: "New SKU has been created.",
              });
            }
          } catch (e) {
            console.error(e);
            notify({ type: "danger", msg: "An error has been occurred." });
          }
        },
      })
      .unsubscribe();
  }, [sku$, notify, skuListQuery, skuUpsertMutation, teamCtx, onClose]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="md" variant="outlined">
        <ModalClose />
        <DialogTitle>{t("Create New SKU")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <XTextInput
                val$={sku$.sku$}
                error$={sku$.sku$error$}
                label={t("SKU")}
                required
              />
              <XTextInput val$={sku$.name$} label={t("Name")} required />
            </Stack>
            <XTextInput val$={sku$.description$} label={t("Description")} />
            <Stack direction="row" spacing={2}>
              <XTextInput val$={sku$.category$} label={t("Category")} />
              <XTextInput val$={sku$.material$} label={t("Material")} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <XTextInput val$={sku$.asin$} label={t("ASIN")} />
              <XTextInput val$={sku$.hs$} label={t("HS")} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <XTextInput val$={sku$.hts$} label={t("HTS")} />
              <XTextInput val$={sku$.upc$} label={t("UPC")} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            fullWidth
            onClick={handleSave}
            disabled={saving}
            loading={loading}
          >
            {t("Save")}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
